import React from "react";
import { Image } from "react-bootstrap";

import Cover from "../../../assets/images/articles/history/image.png";
import Citations from "../Citations";
import { parseCitations } from "../utils/parse-citations";

const citations = [
  {
    id: 1,
    text: "Baker, M. (2016). 1,500 scientists lift the lid on reproducibility. Nature, 533(7604), 452–454. https://doi.org/10.1038/533452a",
  },
];

export default function HistoryOfScientificMethod() {
  return (
    <article
      className="px-3 px-md-4 py-4"
      style={{
        fontSize: "1.05rem",
        lineHeight: 1.8,
        color: "#212529",
        maxWidth: "900px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <header className="text-center mb-5">
        <h2 className="fw-bold mb-3">The History of the Scientific Method: Part 1</h2>
        <p className="text-muted">Introduction & The Crisis of Reproducibility</p>
        <Image
          src={Cover}
          alt="history of scientific method"
          fluid
          rounded
          className="my-4 shadow-sm"
        />
      </header>

      {/* Introduction */}
      <section>
        <p>
          {parseCitations(`
            More than 70% of researchers have tried and failed to reproduce another scientist's experiments, and more than half have failed to reproduce their own. These sobering findings from a 2016 Nature survey of 1,576 researchers helped bring what is now called  the reproducibility crisis into sharp focus [1]. While the scale of the problem feels modern, the underlying issues of replication, documentation, and verification, are as old as scientific inquiry itself, and they strike at the heart of scientific credibility and public trust.`)}
        </p>

        <p>
          Yet the reproducibility crisis is not only a contemporary concern. Throughout history, researchers have struggled with incomplete records, ambiguous protocols, and inconsistent results. Understanding how documentation and methodological rigor evolved across cultures and centuries shows that today’s debates echo long-standing questions about how reliable knowledge is produced. 
        </p>

        <p>
          By examining history, we can better see why transparent, detailed documentation is not administrative overhead, but a foundation of trustworthy science. And why laboratory notebooks remain one of the most essential tools for preventing irreproducibility.
        </p>
      </section>

      <hr className="my-5" />

      {/* Why Study History */}
      <h2 className="fw-bold mt-5 mb-3">Why Study the History of the Scientific Method?</h2>

      <section>
        <p>
          The scientific method did not emerge fully formed. It developed slowly, shaped by practice, argument, and refinement across diverse civilizations. From ancient Egyptian medical records to medieval Islamic optics, from the Scientific Revolution in Europe to today’s open science movements, each period contributed pieces of what we now recognize as scientific method. 
        </p>

        <p>An historical perspective provides four enduring lessons:</p>
        <ul>
          <li><strong>Documentation enables replication:</strong> Ideas that endure are recorded clearly.</li>
          <li><strong>Transparency builds trust:</strong> Independent verification strengthens credibility.</li>
          <li><strong>Community standards evolve:</strong> Best practices shift with context and technology.</li>
          <li><strong>Core principles persist:</strong> Tools change, but rigor and clarity remain essential.</li>
        </ul>

        <p>
          These truths show that modern reproducibility initiatives are not new — they are modern
          reflections of long-standing scientific values.
        </p>
      </section>

      <hr className="my-5" />

      {/* Modern Context */}
      <h2 className="fw-bold mt-5 mb-3">The Modern Context</h2>

      <section>
        <p>
          The reproducibility crisis affects nearly every discipline today: psychology, biomedical
          research, cancer biology, social sciences, and more. Contributing factors include:
        </p>

        <ul>
          <li>Incomplete or selective reporting</li>
          <li>Publication bias toward positive results</li>
          <li>Insufficient sample sizes</li>
          <li>Questionable research practices</li>
          <li>Pressure to publish rapidly</li>
          <li>Lack of detail in methods sections</li>
        </ul>

        <p>
          Interestingly, many proposed solutions — preregistration, detailed protocols, open data,
          independent replication — echo principles championed by early experimentalists like
          Robert Boyle in the 1660s. Rather than a collapse of standards, today’s crisis reflects a
          recurring historical challenge: ensuring scientific reliability.
        </p>
      </section>

      <hr className="my-5" />

      <h2 className="fw-bold mt-5 mb-3">What This Series Covers</h2>

      <section>
        <p>This seven-part series traces the evolution of scientific methodology:</p>
        <ul>
          {/* need to link to other articles */}
          <li>Part 2 – Ancient foundations (Egypt, Greece, Islamic Golden Age)</li>
          <li>Part 3 – Scientific Revolution & formal experimental method</li>
          <li>Part 4 – Rise of laboratory notebooks & professionalization</li>
          <li>Part 5 – 20th-century philosophy & the reproducibility crisis</li>
          <li>Part 6 – Modern solutions (FAIR data, preregistration, open science)</li>
          <li>Part 7 – Future directions for scientific practice</li>
        </ul>

        <p>
          Together, these chapters show that efforts to improve reproducibility stand on centuries
          of hard‑won insights into how reliable knowledge is created and preserved.
        </p>
      </section>

      <blockquote className="blockquote-dark p-4 border-start border-4 border-primary rounded my-5">
        <em>
          The pursuit of reliable knowledge has always depended on clear documentation, transparent
          methods, and a scientific community committed to verification.
        </em>
      </blockquote>

      {/* References */}
      <h3 className="fw-semibold mt-4">References</h3>
      <Citations items={citations} />
    </article>
  );
}
